import { useEffect, useRef, type ReactNode } from 'react';
import { PageCanvas } from '@traceio-org/ui';
import { ADMIN_PRODUCT_NAME } from '../navigation';

/**
 * Every Trace Admin page: the library's PageCanvas plus two page-level duties. It names the browser tab
 * after the page, and when the page opens it moves focus to the page's h1 (each page renders exactly one),
 * so keyboard and screen reader users start at the new content instead of the link they used. Focus moves
 * once per page, never while the page updates (a search must not take focus from its field).
 *
 * Pages read in the library's centred column (PageCanvas 'standard', 1200 px). A dashboard page opts into the full
 * width of the main area with width="full" (PageCanvas 'operations': no maximum, the same padding); only Health does.
 */
export function AdminPage({
  title,
  width = 'column',
  children,
}: {
  title: string;
  width?: 'column' | 'full';
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.title = `${title} | ${ADMIN_PRODUCT_NAME}`;
  }, [title]);

  useEffect(() => {
    const heading = ref.current?.querySelector('h1');
    if (!heading) return;
    // The h1 receives focus programmatically only; tabindex -1 keeps it out of the tab order.
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
  }, []);

  return (
    <div ref={ref} className="admin-page" data-width={width}>
      <PageCanvas density={width === 'full' ? 'operations' : 'standard'}>{children}</PageCanvas>
    </div>
  );
}
